import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, FileText, Calendar, Mic, Book, Users, 
  Heart, HelpCircle, Handshake, Settings, LogOut, Menu, X,
  DollarSign, Mail, BarChart3, Eye, MapPin, MessageSquare,
  Radio, UserCog, Shield, Palette, Store, GraduationCap, ClipboardList, Wand2, BookHeart, MessageCircle, Share2, Megaphone, QrCode, Image, Star
} from 'lucide-react';

// ... rest of imports
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create context for user data
const AdminContext = createContext(null);
export const useAdmin = () => useContext(AdminContext);

// Sidebar links with role requirements
const getSidebarLinks = (isAdmin) => {
  const contentLinks = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
    { icon: FileText, label: 'Sermons', path: '/admin/sermons' },
    { icon: Mic, label: 'Podcasts', path: '/admin/podcasts' },
    { icon: Calendar, label: 'Events', path: '/admin/events' },
    { icon: Book, label: 'Blog', path: '/admin/blog' },
    { icon: Users, label: 'Programs', path: '/admin/programs' },
    { icon: MapPin, label: 'Pantries', path: '/admin/pantries' },
    { icon: MessageSquare, label: 'Small Groups', path: '/admin/small-groups' },
    { icon: Heart, label: 'Prayers', path: '/admin/prayers' },
    { icon: HelpCircle, label: 'Q&A', path: '/admin/qa' },
    { icon: Handshake, label: 'Partners', path: '/admin/partners' },
    { icon: Store, label: 'Businesses', path: '/admin/businesses' },
    { icon: GraduationCap, label: 'Business Teaching', path: '/admin/business-teaching' },
    { icon: Radio, label: 'Livestream', path: '/admin/livestream' },
    { icon: Mail, label: 'Messages', path: '/admin/messages' },
    { icon: Users, label: 'Volunteers', path: '/admin/volunteers' },
    { icon: Eye, label: 'Page Visibility', path: '/admin/pages' },
  ];

  const adminOnlyLinks = [
    { icon: QrCode, label: 'Pantry Check-in', path: '/admin/pantry-system', adminOnly: true },
    { icon: Image, label: 'Media Library', path: '/admin/media-library', adminOnly: true },
    { icon: Star, label: 'Reviews', path: '/admin/reviews', adminOnly: true },
    { icon: Share2, label: 'Content Hub', path: '/admin/content-hub', adminOnly: true },
    { icon: Megaphone, label: 'Marketing', path: '/admin/marketing', adminOnly: true },
    { icon: MapPin, label: 'County Inquiries', path: '/admin/inquiries', adminOnly: true },
    { icon: ClipboardList, label: 'Activity Log', path: '/admin/activity-log', adminOnly: true },
    { icon: Wand2, label: 'Grant Writer', path: '/admin/grants', adminOnly: true },
    { icon: BookHeart, label: 'Impact Stories', path: '/admin/stories', adminOnly: true },
    { icon: MessageCircle, label: 'Suggestions', path: '/admin/suggestions', adminOnly: true },
    { icon: DollarSign, label: 'Donations', path: '/admin/donations', adminOnly: true },
    { icon: UserCog, label: 'Users', path: '/admin/users', adminOnly: true },
    { icon: Palette, label: 'Theme', path: '/admin/theme', adminOnly: true },
    { icon: Settings, label: 'Settings', path: '/admin/settings', adminOnly: true },
  ];

  if (isAdmin) {
    return [...contentLinks, ...adminOnlyLinks];
  }
  return contentLinks;
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'admin' || isOwner;
  const sidebarLinks = getSidebarLinks(isAdmin);

  useEffect(() => {
    // Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setIsLoading(false);
      return;
    }

    if (!user) {
      checkAuth();
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate('/admin/login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out');
      navigate('/admin/login');
    } catch (error) {
      navigate('/admin/login');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AdminContext.Provider value={{ user, isAdmin, isOwner, refreshUser: checkAuth }}>
      <div className="min-h-screen bg-cream flex" data-testid="admin-layout">
        {/* Mobile sidebar toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
          data-testid="sidebar-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-charcoal text-white
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_d52ae5dd-568a-4669-9c8f-951ff5c53e99/artifacts/g3qmizx9_IMG_2894.png" 
                alt="Logo" 
                className="h-10 w-auto brightness-0 invert"
              />
              <div>
                <span className="font-serif text-sm block">Admin Panel</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${isOwner ? 'bg-purple' : isAdmin ? 'bg-burgundy' : 'bg-gold text-charcoal'}`}>
                  {isOwner ? 'Owner' : isAdmin ? 'Admin' : 'User'}
                </span>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto">
            {/* Content Management Section */}
            <p className="text-xs uppercase tracking-wider text-white/40 px-4 pt-2 pb-1">Content</p>
            {sidebarLinks.filter(l => !l.adminOnly).map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors
                    ${isActive 
                      ? 'bg-gold text-charcoal' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'}
                  `}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  <link.icon size={18} />
                  {link.label}
                </Link>
              );
            })}

            {/* Admin Only Section */}
            {isAdmin && (
              <>
                <p className="text-xs uppercase tracking-wider text-white/40 px-4 pt-4 pb-1 flex items-center gap-2">
                  <Shield size={12} /> Admin Only
                </p>
                {sidebarLinks.filter(l => l.adminOnly).map((link) => {
                  const isActive = location.pathname === link.path;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors
                        ${isActive 
                          ? 'bg-burgundy text-white' 
                          : 'text-white/70 hover:bg-burgundy/50 hover:text-white'}
                      `}
                      data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <link.icon size={18} />
                      {link.label}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User & Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-charcoal">
            {user && (
              <div className="flex items-center gap-3 mb-3 px-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                    <span className="text-charcoal font-medium">{user.name?.charAt(0)}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-white/50 truncate">{user.email}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-x-hidden">
          <Outlet context={{ user, isAdmin, isOwner }} />
        </main>
      </div>
    </AdminContext.Provider>
  );
}
